import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import { consoleApi, getErrorMessageFromUnknown, useActiveProject, useAuth } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import type { BillingFormData } from './billing-form-data';
import { stripePayloadWithServiceAddress, stripePayloadWithoutServiceAddress } from './stripe-provider';

const logger = createLogger(APP_SCOPE.aura);

export const useStripeSubmit = (isTrialExtension = false) => {
  // Following three must be curried, otherwise Stripe shits itself.
  const elements = useElements();
  const stripe = useStripe();
  const { data: user } = consoleApi.useGetUserDetailsQuery();
  const { user: authUser } = useAuth();
  const activeProject = useActiveProject();
  const { data: billingAccount } = consoleApi.useGetBillingAccountForProjectQuery(activeProject.id);
  const [addCard] = consoleApi.useAddCardMutation();
  const [createCustomer] = consoleApi.useAddCustomerMutation();
  const [updateCustomer] = consoleApi.useUpdateCustomerDetailsMutation();

  return async (billingFormData: BillingFormData, isServiceAddress = true) => {
    if (isNullish(user) || isNullish(authUser?.profile.email)) {
      return;
    }
    let billingDetails;
    if (isServiceAddress) {
      billingDetails = stripePayloadWithoutServiceAddress(billingFormData, authUser.profile.email);
    } else {
      billingDetails = stripePayloadWithServiceAddress(billingFormData, authUser.profile.email);
    }
    if (isNullish(elements)) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (isNullish(cardElement)) {
      return;
    }

    // We override name here with the cardholders name since this creates the token for the card element.
    // We then update the customers name with company name which differs from the cardholder (shipping name).
    const payload = await stripe?.createToken(cardElement, {
      ...billingDetails,
      name: billingFormData.name,
    });

    if (isNotNullish(payload?.error)) {
      throw payload.error;
    }
    if (isNullish(payload)) {
      return;
    }
    try {
      if (isNotNullish(billingAccount) && billingAccount.isFunded) {
        if (isTrialExtension) {
          // If we arrive here, something has gone wrong.
          throw new Error('Cannot extend trial with existing billing details');
        }
        await addCard({ projectId: activeProject.id, token: payload }).unwrap();
        await updateCustomer({
          projectId: activeProject.id,
          shipping: billingDetails.shipping,
          address: billingDetails.address,
          name: billingDetails.name,
        }).unwrap();
      } else {
        await createCustomer({
          projectId: activeProject.id,
          token: payload,
          is_trial_extension: isTrialExtension,
          shipping: billingDetails.shipping,
          address: billingDetails.address,
          name: billingDetails.name,
          email: billingDetails.email,
        }).unwrap();
      }
    } catch (e: unknown) {
      const errorMessage = getErrorMessageFromUnknown(e);
      logger.error(errorMessage);
      throw e;
    }
  };
};
